import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAppraisalData,
  getQuestionsByCategory
} from "../../store/selectors";
import { appraisalSlice } from "../../store/slices/Appraisal";
import ConditionAnswersReview from "./ConditionAnswersReview";
import { getPhotosQuantity } from "./helpers";
import { useNavigate } from "react-router-dom";
import { imageUploadSlice } from "../../store/slices/ImageUploads";
import { useSourceSystem } from "../../hooks/useSourceSystem";
import {
  ConditionalQuestionsFromAssumptionsIds,
  MultipleChoiceQuestionIds
} from "../../data/QuestionData";
import { QuestionIds, yesMaskValue } from "../../data/QuestionPresentation";
import {
  getInitialCheckboxState,
  validateCheckboxAnswers
} from "../../components/AssumptionQuestions/helpers";
import { getPhotoQuantityMultipleChoice, hasAnswersChanged } from "../helpers";

export default function ConditionAnswersReviewContainer() {
  const { questionsAndAnswers, photosRequired } = useSelector(getAppraisalData);
  const conditionQuestions = useSelector(getQuestionsByCategory("Condition"));
  const assumptionQuestions = useSelector(getQuestionsByCategory("Assumption"));
  const [answersChanged, setAnswersChanged] = useState(false);
  const [originalAnswers] = useState(questionsAndAnswers);
  const [selectedQuestionsHaveChanged, setSelectedQuestionsHaveChanged] =
    useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    sourceSystem: { showAssumptionQuestionsWithCondition }
  } = useSourceSystem();
  const glassQuestion = assumptionQuestions.find(
    (question) => question.id === QuestionIds.GLASS
  );

  const [checkboxState, setCheckboxState] = useState(
    getInitialCheckboxState(MultipleChoiceQuestionIds, questionsAndAnswers)
  );

  const extraConditionQuestionsFromAssumptions = assumptionQuestions.filter(
    (question) =>
      ConditionalQuestionsFromAssumptionsIds.find((id) => question.id === id)
  );

  const questionsToRender = showAssumptionQuestionsWithCondition
    ? conditionQuestions.concat(extraConditionQuestionsFromAssumptions)
    : conditionQuestions;

  useEffect(() => {
    const combinedNewAnswers = {
      ...questionsAndAnswers,
      ...selectedQuestionsHaveChanged,
      ...checkboxState
    };
    const answersChanged = hasAnswersChanged(
      originalAnswers,
      combinedNewAnswers
    );
    setAnswersChanged(answersChanged);
  }, [selectedQuestionsHaveChanged, originalAnswers, questionsAndAnswers]);

  function handleChange(questionId, answerId, showCheckboxValue) {
    setAnswersChanged(true);
    setSelectedQuestionsHaveChanged({
      ...selectedQuestionsHaveChanged,
      [questionId]: [answerId]
    });
    if (MultipleChoiceQuestionIds?.includes(questionId)) {
      dispatch(
        appraisalSlice.actions.updateConditionalAnswersSubmitted(questionId)
      );
      setCheckboxState({
        ...checkboxState,
        [questionId]: [answerId]
      });
    }
  }

  const handleCheckboxChange = (questionId, answerId, checked) => {
    dispatch(
      appraisalSlice.actions.updateConditionalAnswersSubmitted(questionId)
    );
    if (checkboxState[questionId][0] === yesMaskValue) {
      setSelectedQuestionsHaveChanged({
        ...selectedQuestionsHaveChanged,
        [questionId]: [answerId]
      });
      setCheckboxState({
        ...checkboxState,
        [questionId]: [answerId]
      });
      return;
    }
    if (checked) {
      const checkboxAnswerAdded = [...checkboxState[questionId], answerId];
      setCheckboxState({
        ...checkboxState,
        [questionId]: checkboxAnswerAdded
      });
      setSelectedQuestionsHaveChanged({
        ...selectedQuestionsHaveChanged,
        [questionId]: checkboxAnswerAdded
      });
    } else {
      const checkboxAnswerRemoved = [...checkboxState[questionId]].filter(
        (answer) => answer !== answerId
      );
      setSelectedQuestionsHaveChanged({
        ...selectedQuestionsHaveChanged,
        [questionId]: checkboxAnswerRemoved
      });
      setCheckboxState({
        ...checkboxState,
        [questionId]: checkboxAnswerRemoved
      });
    }
  };

  const getSelsectedQuestionsChangedWithPhotosRequired = (
    selectedQuestionsHaveChanged,
    conditionQuestions
  ) => {
    let selectQuestionsChangedWithPhotosRequired = {};
    let photosQty;
    for (const [questionId, answers] of Object.entries(
      selectedQuestionsHaveChanged
    )) {
      if (parseInt(questionId) === QuestionIds.MISSINGITEMS) {
        return;
      } else if (parseInt(questionId) === QuestionIds.GLASS) {
        const glassQuestion = conditionQuestions.find(
          (question) => question.id === parseInt(questionId)
        );
        if (answers[0] === glassQuestion.selectedAnswer) {
          photosQty = 0;
        } else {
          photosQty = getPhotoQuantityMultipleChoice(
            glassQuestion.answers,
            answers
          );
        }
      } else {
        photosQty = getPhotosQuantity(
          conditionQuestions,
          parseInt(questionId),
          answers[0]
        );
      }
      selectQuestionsChangedWithPhotosRequired = {
        ...selectQuestionsChangedWithPhotosRequired,
        [questionId]: photosQty
      };
    }
    return selectQuestionsChangedWithPhotosRequired;
  };

  const handleClick = () => {
    if (glassQuestion) {
      dispatch(
        appraisalSlice.actions.setConditionalAnswersSubmitted([
          QuestionIds.MISSINGITEMS,
          QuestionIds.GLASS
        ])
      );
      if (!validateCheckboxAnswers(checkboxState)) {
        return;
      }
    }
    if (answersChanged) {
      dispatch(appraisalSlice.actions.setAnswersChanged(true));
    }
    dispatch(
      appraisalSlice.actions.setQuestionsAndAnswers({
        ...selectedQuestionsHaveChanged,
        ...checkboxState
      })
    );
    const selectedQuestionsChangedWithPhotosRequired =
      getSelsectedQuestionsChangedWithPhotosRequired(
        selectedQuestionsHaveChanged,
        questionsToRender
      );

    dispatch(imageUploadSlice.actions.setReturnToGallery(false));

    dispatch(
      appraisalSlice.actions.setPhotosRequired({
        ...photosRequired,
        ...selectedQuestionsChangedWithPhotosRequired
      })
    );

    navigate("/photos-intro");
  };

  return (
    <ConditionAnswersReview
      questionsAndAnswers={questionsAndAnswers}
      conditionQuestions={questionsToRender}
      handleChange={handleChange}
      answersChanged={answersChanged}
      handleClick={handleClick}
      selectedQuestionsHaveChanged={selectedQuestionsHaveChanged}
      checkboxState={checkboxState}
      handleCheckboxChange={handleCheckboxChange}
    />
  );
}
